<script lang="ts">
  import { onMount } from 'svelte';
  import { listUnlinkedProductCompetitorServices } from '@shared/api/products';
  import type {
    ProductCompetitorService,
    UnlinkedProductCompetitorService,
    UnlinkedProductCompetitorServiceList,
  } from '@shared/types/product-competitor-service';
  import RelinkProductCompetitorModal from './RelinkProductCompetitorModal.svelte';

  interface Props {
    canEdit?: boolean;
  }

  const { canEdit = false }: Props = $props();

  let items = $state<UnlinkedProductCompetitorService[]>([]);
  let total = $state(0);
  let loading = $state(true);
  let hasLoaded = $state(false);
  let error = $state<string | null>(null);
  let selectedRow = $state<UnlinkedProductCompetitorService | null>(null);
  let loadGeneration = 0;

  $effect(() => {
    if (!canEdit) selectedRow = null;
  });

  onMount(() => {
    void load();
    return () => {
      loadGeneration += 1;
    };
  });

  async function load(): Promise<void> {
    const generation = ++loadGeneration;
    loading = true;
    hasLoaded = false;
    error = null;
    try {
      const response = await listUnlinkedProductCompetitorServices();
      if (generation !== loadGeneration) return;
      items = response.items;
      total = response.total;
      hasLoaded = true;
    } catch (loadError) {
      if (generation !== loadGeneration) return;
      error =
        loadError instanceof Error
          ? loadError.message
          : '未紐付け競合カードの読み込みに失敗しました。';
    } finally {
      if (generation === loadGeneration) loading = false;
    }
  }

  function startRelink(row: UnlinkedProductCompetitorService): void {
    if (!canEdit) return;
    selectedRow = row;
  }

  function handleRelinked(updated: ProductCompetitorService): void {
    const resolvedId = updated.id;
    if (items.some((item) => item.id === resolvedId)) {
      items = items.filter((item) => item.id !== resolvedId);
      total = Math.max(0, total - 1);
    }
    selectedRow = null;
  }

  async function handleConflict(rowId: string): Promise<boolean | null> {
    // Join the same generation sequence as load(): an in-flight retry must
    // not overwrite this fresher snapshot afterwards, and vice versa.
    const generation = ++loadGeneration;
    let response: UnlinkedProductCompetitorServiceList;
    try {
      response = await listUnlinkedProductCompetitorServices();
    } catch (refreshError) {
      if (generation !== loadGeneration) return null;
      loading = false;
      throw refreshError;
    }
    const stillUnlinked = response.items.some((item) => item.id === rowId);
    if (generation !== loadGeneration) return null;

    items = response.items;
    total = response.total;
    hasLoaded = true;
    error = null;
    loading = false;
    if (!stillUnlinked) selectedRow = null;
    return stillUnlinked;
  }

  function cardTitle(row: UnlinkedProductCompetitorService): string {
    return row.title && row.title.length > 0 ? row.title : '（名称なし）';
  }

  function hasMemo(row: UnlinkedProductCompetitorService): boolean {
    return (row.memo?.length ?? 0) > 0;
  }
</script>

<section
  class="panel unlinked-panel"
  aria-labelledby="unlinked-product-competitors-title"
  data-testid="unlinked-product-competitors-panel"
>
  <header class="panel-head">
    <div>
      <h2 id="unlinked-product-competitors-title">未紐付け競合カード</h2>
      <p>過去の競合カードを既存の外部ソリューションへ手動で紐付けます。</p>
    </div>
    <span class="count" data-testid="unlinked-product-competitor-count"
      >{hasLoaded ? `${total}件` : '—'}</span
    >
  </header>

  {#if loading}
    <p class="panel-state" data-testid="unlinked-product-competitors-loading">
      未紐付け競合カードを読み込んでいます。
    </p>
  {:else if error}
    <div class="panel-state error-state" role="alert">
      <p>{error}</p>
      <button
        type="button"
        class="btn"
        onclick={() => void load()}
        data-testid="unlinked-product-competitors-retry"
      >
        再試行
      </button>
    </div>
  {:else if items.length === 0}
    <p class="panel-state" data-testid="unlinked-product-competitors-empty">
      未紐付け競合カードはありません。
    </p>
  {:else}
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>自社ソリューション</th>
            <th>既存カード名</th>
            <th>メモ</th>
            {#if canEdit}<th class="action-head">操作</th>{/if}
          </tr>
        </thead>
        <tbody>
          {#each items as item (item.id)}
            <tr data-testid="unlinked-product-competitor-row">
              <td>
                <a href={`/products/${encodeURIComponent(item.product_id)}`}>
                  {item.product_name}
                </a>
              </td>
              <td class="card-title">{cardTitle(item)}</td>
              <td>
                <span
                  class:has-memo={hasMemo(item)}
                  class="memo-status"
                  aria-label={hasMemo(item) ? 'メモあり' : 'メモなし'}
                >
                  {hasMemo(item) ? 'あり' : 'なし'}
                </span>
              </td>
              {#if canEdit}
                <td class="action-cell">
                  <button
                    type="button"
                    class="btn"
                    onclick={() => startRelink(item)}
                    data-testid="unlinked-product-competitor-relink"
                  >
                    手動で紐付け
                  </button>
                </td>
              {/if}
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<RelinkProductCompetitorModal
  open={selectedRow !== null}
  row={selectedRow}
  {canEdit}
  onClose={() => (selectedRow = null)}
  onRelinked={handleRelinked}
  onConflict={handleConflict}
/>

<style>
  .unlinked-panel {
    display: grid;
    gap: 14px;
    margin-bottom: 12px;
  }
  .panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }
  .panel-head h2 {
    margin: 0;
    color: #111827;
    font-size: 1rem;
  }
  .panel-head p {
    margin: 4px 0 0;
    color: var(--muted, #6b7280);
    font-size: 0.8125rem;
  }
  .count {
    flex: none;
    padding: 3px 9px;
    border-radius: 999px;
    background: #fef3c7;
    color: #92400e;
    font-size: 0.8125rem;
    font-weight: 700;
  }
  .panel-state {
    margin: 0;
    padding: 18px;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    background: #f8fafc;
    color: #64748b;
    font-size: 0.875rem;
    text-align: center;
  }
  .error-state {
    display: grid;
    justify-items: center;
    gap: 10px;
    color: #b91c1c;
  }
  .error-state p {
    margin: 0;
  }
  .table-wrap {
    overflow-x: auto;
    border: 1px solid var(--border, #e5e7eb);
    border-radius: 6px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8125rem;
  }
  th,
  td {
    padding: 10px 12px;
    text-align: left;
    vertical-align: middle;
    border-bottom: 1px solid var(--border, #e5e7eb);
  }
  th {
    background: #f8fafc;
    color: #475569;
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
  }
  tbody tr:last-child td {
    border-bottom: 0;
  }
  td a {
    color: #2563eb;
    font-weight: 700;
    text-decoration: none;
  }
  td a:hover {
    text-decoration: underline;
  }
  .card-title {
    min-width: 180px;
    color: #111827;
    overflow-wrap: anywhere;
  }
  .memo-status {
    display: inline-flex;
    padding: 2px 7px;
    border-radius: 999px;
    background: #f1f5f9;
    color: #64748b;
    font-size: 0.75rem;
    font-weight: 700;
  }
  .memo-status.has-memo {
    background: #dcfce7;
    color: #166534;
  }
  .action-head,
  .action-cell {
    width: 1%;
    text-align: right;
    white-space: nowrap;
  }
  .action-cell .btn {
    font-size: 0.75rem;
  }
  @media (max-width: 720px) {
    .panel-head {
      align-items: center;
    }
    th,
    td {
      padding: 9px 10px;
    }
  }
</style>
